import React, { useMemo, useState } from "react";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Button, Segmented } from "../../../../design-system";
import { PageHeader } from "../components/PageHeader";
import { StatusTag } from "../components/StatusTag";
import { useTailSpend } from "../TailSpendContext";
import { Connector, ConnectorEvent } from "../model/types";
import classes from "./SetupView.module.less";

const EVENT_YEAR = 2026;

const parseWhen = (label: string): { date: string; time: string } => {
    const parts = label.split("·").map((s) => s.trim());
    return { date: parts[0] || label, time: parts[1] || "" };
};

const formatDateHeader = (date: string): string => {
    if (date.toLowerCase() === "today") return "Today";
    if (date.toLowerCase() === "mon" || date.toLowerCase() === "live") return date;
    return /\d{4}/.test(date) ? date : `${date}, ${EVENT_YEAR}`;
};

function groupByDay(events: ConnectorEvent[]): Array<{
    date: string;
    rows: Array<{ event: ConnectorEvent; time: string }>;
}> {
    const out: Array<{ date: string; rows: Array<{ event: ConnectorEvent; time: string }> }> = [];
    let current: (typeof out)[number] | null = null;
    for (const event of events) {
        const { date, time } = parseWhen(event.whenLabel);
        if (!current || current.date !== date) {
            current = { date, rows: [] };
            out.push(current);
        }
        current.rows.push({ event, time });
    }
    return out;
}

type DirectionFilter = "all" | "out" | "in";

const dealPath = (id: string) => `/superadmin/demo/tail-spend/negotiations/${id}`;

const statusTone = (s: Connector["status"]) =>
    s === "active" ? "green" : s === "error" ? "red" : "slate";
const statusLabel = (s: Connector["status"]) =>
    s === "active" ? "Active" : s === "error" ? "Error" : "Disconnected";

export const SetupView: React.FC = () => {
    const { workspace } = useTailSpend();
    const navigate = useNavigate();
    const [selectedId, setSelectedId] = useState(workspace.connectors[0]?.id);
    const [direction, setDirection] = useState<DirectionFilter>("all");

    const connector = workspace.connectors.find((c) => c.id === selectedId);

    const events = useMemo(() => {
        if (!connector) return [] as ConnectorEvent[];
        const all = workspace.systemEvents.filter(
            (e) => e.connectorId === connector.id
        );
        if (direction === "all") return all;
        return all.filter((e) => e.direction === direction);
    }, [connector, direction, workspace.systemEvents]);

    return (
        <>
            <PageHeader
                title="Setup"
                subtitle="The systems the agent reads from and writes back to. Status, throughput, and the flow of events in and out of each one."
            />

            <div className={classes.layout}>
                <aside className={classes.sidebar}>
                    <div className={classes.sidebarHead}>Connected systems</div>
                    {workspace.connectors.map((c) => {
                        const selected = c.id === selectedId;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                className={`${classes.sidebarItem} ${
                                    selected ? classes.sidebarItemSelected : ""
                                }`}
                                onClick={() => setSelectedId(c.id)}
                            >
                                <span
                                    className={classes.logo}
                                    style={{ background: c.logoColor }}
                                >
                                    {c.logoLabel}
                                </span>
                                <span className={classes.sidebarName}>{c.name}</span>
                                <span
                                    className={`${classes.dot} ${classes[c.status]}`}
                                />
                            </button>
                        );
                    })}
                </aside>

                {connector && (
                    <section className={classes.detail}>
                        <div className={classes.detailHead}>
                            <div className={classes.detailHeadMain}>
                                <span
                                    className={classes.detailLogo}
                                    style={{ background: connector.logoColor }}
                                >
                                    {connector.logoLabel}
                                </span>
                                <div>
                                    <div className={classes.detailName}>
                                        {connector.name}
                                    </div>
                                    <div className={classes.detailSub}>
                                        {connector.detail}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={classes.stats}>
                            <Stat label="Status">
                                <StatusTag tone={statusTone(connector.status)}>
                                    {statusLabel(connector.status)}
                                </StatusTag>
                            </Stat>
                            <Stat label="Last sync" value={connector.lastSyncedLabel} />
                            <Stat
                                label="Events today"
                                value={connector.eventsToday.toLocaleString()}
                            />
                            <Stat
                                label="Avg latency"
                                value={`${connector.avgLatencyMs} ms`}
                            />
                            {connector.account && (
                                <Stat
                                    label="Account"
                                    value={connector.account}
                                    wide
                                />
                            )}
                        </div>

                        <div className={classes.eventsHead}>
                            <span className={classes.eventsTitle}>Events</span>
                            <Segmented
                                value={direction}
                                onChange={(v) => setDirection(v as DirectionFilter)}
                                options={[
                                    { label: "All", value: "all" },
                                    {
                                        label: (
                                            <span>
                                                <ArrowUpOutlined /> Outbound
                                            </span>
                                        ),
                                        value: "out",
                                    },
                                    {
                                        label: (
                                            <span>
                                                <ArrowDownOutlined /> Inbound
                                            </span>
                                        ),
                                        value: "in",
                                    },
                                ]}
                            />
                        </div>

                        <div className={classes.eventsList}>
                            {events.length === 0 ? (
                                <div className={classes.empty}>
                                    No events match this filter.
                                </div>
                            ) : (
                                groupByDay(events).map((g) => (
                                    <div key={g.date} className={classes.dayGroup}>
                                        <div className={classes.dayHeader}>
                                            {formatDateHeader(g.date)}
                                        </div>
                                        {g.rows.map(({ event: e, time }) => (
                                            <div key={e.id} className={classes.event}>
                                                <span
                                                    className={`${classes.eventDir} ${
                                                        classes[e.direction]
                                                    }`}
                                                >
                                                    {e.direction === "out" ? (
                                                        <ArrowUpOutlined />
                                                    ) : (
                                                        <ArrowDownOutlined />
                                                    )}
                                                </span>
                                                <span className={classes.eventWhen}>
                                                    {time}
                                                </span>
                                                <div className={classes.eventBody}>
                                                    <div className={classes.eventTitle}>
                                                        {e.title}
                                                    </div>
                                                    {e.detail && (
                                                        <div className={classes.eventDetail}>
                                                            {e.detail}
                                                        </div>
                                                    )}
                                                </div>
                                                {e.dealId && (
                                                    <Button
                                                        type="primary"
                                                        className={classes.openBtn}
                                                        onClick={() =>
                                                            navigate(dealPath(e.dealId!))
                                                        }
                                                    >
                                                        Negotiation
                                                    </Button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))
                            )}
                        </div>
                    </section>
                )}
            </div>
        </>
    );
};

const Stat: React.FC<{
    label: string;
    value?: string;
    children?: React.ReactNode;
    wide?: boolean;
}> = ({ label, value, children, wide }) => (
    <div className={`${classes.stat} ${wide ? classes.statWide : ""}`}>
        <div className={classes.statLabel}>{label}</div>
        <div className={classes.statValue}>{children ?? value}</div>
    </div>
);
